import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Tooltip,
  Select,
  DatePicker,
  Form,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined,
  HeartFilled,
  HeartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { NewOrderModalContext } from '../../../layout/MainLayout';
import OrderDeleteModal from '../delete/OrderDeleteModal';
import './Orders.css';
import { canOrderDelete, canOrderEdit, isAdmin, isManager } from '../../../utils/userRoles';
import { DiamondFilled, DiamondOutlined } from '../../../components/DiamondIcon/DiamondIcon';

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const SOURCE_OPTIONS = [
  { value: 'K Etsy', label: 'K Etsy' },
  { value: 'Anirudh Etsy', label: 'Anirudh Etsy' },
  { value: 'Website', label: 'Website' },
  { value: 'Others', label: 'Others' },
];

const ORDER_READY_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'Incomplete', label: 'Incomplete' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'ready', label: 'Ready (send to QC)' },
  { value: 'repair', label: 'Repair' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'return', label: 'Return' },
];

const STATUS_COLORS = {
  open: 'blue',
  pending: 'orange',
  ready: 'green',
  repair: 'magenta',
  dispatch: 'cyan',
  completed: 'green',
  cancelled: 'red',
  return: 'purple',
};

const DATE_FORMAT = 'YYYY-MM-DD';

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const emptyFilters = () => ({
  status: undefined,
  orderDateStart: undefined,
  orderDateEnd: undefined,
  targetDateStart: undefined,
  targetDateEnd: undefined,
  sourceOfOrder: undefined,
  modeOfParcel: undefined,
  orderReady: undefined,
  isFavoriteOnly: undefined,
  isStoneMarkedOnly: undefined,
  isImportantOnly: undefined,
});

const ORDERS_LIST_STORAGE_KEY = 'kosha_orders_list_v1';

function serializeFiltersForStorage(filters) {
  const f = { ...emptyFilters(), ...filters };
  const toDateStr = (d) => (d && dayjs(d).isValid() ? dayjs(d).format(DATE_FORMAT) : undefined);
  return {
    status: f.status,
    orderDateStart: toDateStr(f.orderDateStart),
    orderDateEnd: toDateStr(f.orderDateEnd),
    targetDateStart: toDateStr(f.targetDateStart),
    targetDateEnd: toDateStr(f.targetDateEnd),
    sourceOfOrder: f.sourceOfOrder,
    modeOfParcel: f.modeOfParcel,
    orderReady: f.orderReady,
    isFavoriteOnly: f.isFavoriteOnly === true ? true : undefined,
    isStoneMarkedOnly: f.isStoneMarkedOnly === true ? true : undefined,
    isImportantOnly: f.isImportantOnly === true ? true : undefined,
  };
}

function deserializeFiltersFromStorage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = emptyFilters();
  if (Array.isArray(raw.status)) {
    const ok = raw.status.filter((s) => typeof s === 'string' && STATUS_OPTIONS.some((o) => o.value === s));
    if (ok.length) out.status = ok;
  }
  const parseD = (s) => {
    if (s == null || typeof s !== 'string') return undefined;
    const d = dayjs(s, DATE_FORMAT, true);
    return d.isValid() ? d : undefined;
  };
  out.orderDateStart = parseD(raw.orderDateStart);
  out.orderDateEnd = parseD(raw.orderDateEnd);
  out.targetDateStart = parseD(raw.targetDateStart);
  out.targetDateEnd = parseD(raw.targetDateEnd);
  if (raw.sourceOfOrder && typeof raw.sourceOfOrder === 'string' && SOURCE_OPTIONS.some((o) => o.value === raw.sourceOfOrder)) {
    out.sourceOfOrder = raw.sourceOfOrder;
  }
  if (raw.modeOfParcel != null && typeof raw.modeOfParcel === 'string') {
    const t = raw.modeOfParcel.trim();
    if (t) out.modeOfParcel = t.slice(0, 500);
  }
  if (raw.orderReady && typeof raw.orderReady === 'string' && ORDER_READY_OPTIONS.some((o) => o.value === raw.orderReady)) {
    out.orderReady = raw.orderReady;
  }
  if (raw.isFavoriteOnly === true) out.isFavoriteOnly = true;
  if (raw.isStoneMarkedOnly === true) out.isStoneMarkedOnly = true;
  if (raw.isImportantOnly === true) out.isImportantOnly = true;
  return out;
}

function loadOrdersListPrefs() {
  try {
    const raw = localStorage.getItem(ORDERS_LIST_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o.v !== 1 || !o.filters || typeof o.filters !== 'object') return null;
    const filters = deserializeFiltersFromStorage(o.filters);
    if (!filters) return null;
    const search = typeof o.search === 'string' ? o.search : '';
    const pageSize = [10, 20, 50].includes(Number(o.pageSize)) ? Number(o.pageSize) : DEFAULT_PAGE_SIZE;
    const current = typeof o.current === 'number' && o.current >= 1 ? Math.floor(o.current) : 1;
    return { filters, search, pageSize, current };
  } catch {
    return null;
  }
}

function saveOrdersListPrefs(filters, searchApplied, paginationSlice) {
  try {
    localStorage.setItem(
      ORDERS_LIST_STORAGE_KEY,
      JSON.stringify({
        v: 1,
        filters: serializeFiltersForStorage(filters),
        search: searchApplied ?? '',
        pageSize: paginationSlice.pageSize,
        current: paginationSlice.current,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

const isTargetDatePast = (targetDate) => {
  if (!targetDate) return false;
  const d = dayjs(targetDate).startOf('day');
  return d.isValid() && d.isBefore(dayjs().startOf('day'));
};

export default function Orders() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.authReducer);
  const canToggleFavorite = isAdmin(user) || isManager(user);
  const canToggleStoneMark = canToggleFavorite;
  const { openNewOrderModal, registerRefetchOrders } = useContext(NewOrderModalContext) || {};
  const canCreateOrder = user?.roles?.some((r) => r.value === 'admin' || r.value === 'manager');

  // Initial filters: URL status wins; else role-based default; otherwise no filter
  const initialStatusFromUrl = searchParams.get('status') || undefined;
  const getInitialFilters = () => {
    const f = emptyFilters();
    if (initialStatusFromUrl && STATUS_OPTIONS.some((o) => o.value === initialStatusFromUrl)) {
      f.status = [initialStatusFromUrl];
      return f;
    }
    if(user?.roles?.some((r) => r.value === 'admin' || r.value === 'manager' || r.value === 'read-only')) {
      return f;
    }
    const roleValues = user?.roles?.map((r) => r.value) ?? [];
    if (roleValues.includes('stock-manager')) {
      f.status = ['dispatch'];
    } else if (roleValues.some((v) => v === 'order-manager' || v === 'stone-manager')) {
      f.status = ['open', 'pending','repair'];
    } else if (roleValues.includes('qc-manager')) {
      f.status = ['ready'];
    }
    return f;
  };

  const resolveInitialListState = () => {
    if (initialStatusFromUrl && STATUS_OPTIONS.some((o) => o.value === initialStatusFromUrl)) {
      const f = emptyFilters();
      f.status = [initialStatusFromUrl];
      return {
        filters: f,
        searchInput: '',
        searchApplied: '',
        pagination: { current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 },
      };
    }
    const saved = loadOrdersListPrefs();
    if (saved) {
      return {
        filters: saved.filters,
        searchInput: saved.search,
        searchApplied: saved.search,
        pagination: { current: saved.current, pageSize: saved.pageSize, total: 0 },
      };
    }
    return {
      filters: getInitialFilters(),
      searchInput: '',
      searchApplied: '',
      pagination: { current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 },
    };
  };

  const initialList = resolveInitialListState();
  const lastDebouncedSearchRef = useRef(initialList.searchApplied);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(initialList.pagination);
  const [searchInput, setSearchInput] = useState(initialList.searchInput);
  const [searchApplied, setSearchApplied] = useState(initialList.searchApplied);
  const [appliedFilters, setAppliedFilters] = useState(initialList.filters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState(initialList.filters);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [orderImagePreview, setOrderImagePreview] = useState(null);
  const [favoriteBusyId, setFavoriteBusyId] = useState(null);
  const [stoneBusyId, setStoneBusyId] = useState(null);
  const [importantBusyId, setImportantBusyId] = useState(null);

  // Clear URL param after reading it so back/forward nav stays clean
  useEffect(() => {
    if (searchParams.has('status')) {
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const fetchOrders = useCallback(
    async (page, limit, search, filters = {}) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (search && search.trim()) params.search = search.trim();
        if (filters.status?.length) {
          params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
        }
        if (filters.sourceOfOrder) params.sourceOfOrder = filters.sourceOfOrder;
        if (filters.orderReady) params.orderReady = filters.orderReady;
        if (filters.modeOfParcel && filters.modeOfParcel.trim()) params.modeOfParcel = filters.modeOfParcel.trim();
        if (filters.orderDateStart) params.orderDateStart = dayjs(filters.orderDateStart).format(DATE_FORMAT);
        if (filters.orderDateEnd) params.orderDateEnd = dayjs(filters.orderDateEnd).format(DATE_FORMAT);
        if (filters.targetDateStart) params.targetDateStart = dayjs(filters.targetDateStart).format(DATE_FORMAT);
        if (filters.targetDateEnd) params.targetDateEnd = dayjs(filters.targetDateEnd).format(DATE_FORMAT);
        if (filters.isFavoriteOnly === true) params.isFavorite = 'true';
        if (filters.isStoneMarkedOnly === true) params.isStoneMarked = 'true';
        if (filters.isImportantOnly === true) params.isImportant = 'true';
        const res = await axios.get('/orders', { params });
        setOrders(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to load orders';
        dispatch(setMessage({ message: msg, type: 'error' }));
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const refetch = useCallback(() => {
    fetchOrders(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
  }, [fetchOrders, pagination.current, pagination.pageSize, searchApplied, appliedFilters]);

  useEffect(() => {
    fetchOrders(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
  }, [pagination.current, pagination.pageSize, searchApplied, appliedFilters, fetchOrders]);

  useEffect(() => {
    if (registerRefetchOrders) {
      registerRefetchOrders(refetch);
      return () => registerRefetchOrders(null);
    }
  }, [registerRefetchOrders, refetch]);

  useEffect(() => {
    saveOrdersListPrefs(appliedFilters, searchApplied, {
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [appliedFilters, searchApplied, pagination.current, pagination.pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      const searchChanged = next !== lastDebouncedSearchRef.current;
      lastDebouncedSearchRef.current = next;
      setSearchApplied(next);
      if (searchChanged) {
        setPagination((prev) => ({ ...prev, current: 1 }));
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openFilterModal = () => {
    setModalFilters({ ...appliedFilters });
    setFilterModalOpen(true);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...modalFilters });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    const empty = emptyFilters();
    setModalFilters(empty);
    setAppliedFilters(empty);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const isEmptyFilterValue = (v) =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  const hasActiveFilters = Object.values(appliedFilters).some((v) => !isEmptyFilterValue(v));

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const openProductImagePreview = (urls) => {
    const list = (Array.isArray(urls) ? urls : [])
      .filter((u) => typeof u === 'string' && u.trim())
      .map((u) => u.trim());
    if (!list.length) return;
    setOrderImagePreview({ urls: list, index: 0 });
  };

  const handleToggleFavorite = async (order, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!canToggleFavorite) return;
    if (!order?._id || favoriteBusyId === order._id) return;
    const next = !order.isFavorite;
    setFavoriteBusyId(order._id);
    setOrders((rows) =>
      rows.map((r) => (r._id === order._id ? { ...r, isFavorite: next } : r))
    );
    try {
      await axios.patch(`/orders/${order._id}/favorite`, { isFavorite: next });
    } catch (err) {
      setOrders((rows) =>
        rows.map((r) => (r._id === order._id ? { ...r, isFavorite: !next } : r))
      );
      const msg = err.response?.data?.error || 'Could not update custom order';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setFavoriteBusyId(null);
    }
  };

  const handleToggleStoneMark = async (order, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!canToggleStoneMark) return;
    if (!order?._id || stoneBusyId === order._id) return;
    const next = !order.isStoneMarked;
    setStoneBusyId(order._id);
    setOrders((rows) =>
      rows.map((r) => (r._id === order._id ? { ...r, isStoneMarked: next } : r))
    );
    try {
      await axios.patch(`/orders/${order._id}/stone`, { isStoneMarked: next });
    } catch (err) {
      setOrders((rows) =>
        rows.map((r) => (r._id === order._id ? { ...r, isStoneMarked: !next } : r))
      );
      const msg = err.response?.data?.error || 'Could not update stone mark';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setStoneBusyId(null);
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
  };

  const handleToggleImportant = async (order, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!order?._id || importantBusyId === order._id) return;
    const next = !order.isImportant;
    setImportantBusyId(order._id);
    setOrders((rows) =>
      rows.map((r) => (r._id === order._id ? { ...r, isImportant: next } : r))
    );
    try {
      await axios.patch(`/orders/${order._id}/important`, { isImportant: next });
      if (next === false && appliedFilters.isImportantOnly === true) {
        setOrders((rows) => rows.filter((r) => r._id !== order._id));
      }
    } catch (err) {
      setOrders((rows) =>
        rows.map((r) => (r._id === order._id ? { ...r, isImportant: !next } : r))
      );
      const msg = err.response?.data?.error || 'Could not update favourite';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setImportantBusyId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/orders/${orderToDelete._id}`);
      dispatch(setMessage({ message: 'Order deleted successfully', type: 'success' }));
      setOrderToDelete(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: '',
      key: 'important',
      width: 10,
      fixed: 'left',
      className: 'orders-col-important',
      render: (_, record) => {
        const on = record.isImportant === true;
        return (
          <Tooltip title={on ? 'Remove from favourites' : 'Mark as favourite'}>
            <button
              type="button"
              className={`orders-important-btn${on ? ' orders-important-btn--on' : ''}`}
              aria-label={on ? 'Remove from favourites' : 'Mark as favourite'}
              aria-pressed={on}
              disabled={importantBusyId === record._id}
              onClick={(e) => handleToggleImportant(record, e)}
            >
              {on ? <HeartFilled /> : <HeartOutlined />}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'favorite',
      width: 10,
      fixed: 'left',
      className: 'orders-col-favorite',
      render: (_, record) => {
        const on = record.isFavorite === true;
        return (
          <Tooltip title={canToggleFavorite ? (on ? 'Remove custom order' : 'Mark as custom order') : 'Only admin/manager can change custom order'}>
            <button
              type="button"
              className={`orders-favorite-btn${on ? ' orders-favorite-btn--on' : ''}`}
              aria-label={on ? 'Remove custom order' : 'Mark as custom order'}
              aria-pressed={on}
              disabled={!canToggleFavorite || favoriteBusyId === record._id}
              onClick={(e) => handleToggleFavorite(record, e)}
            >
              {on ? <StarFilled /> : <StarOutlined />}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'stone',
      width: 10,
      fixed: 'left',
      className: 'orders-col-stone',
      render: (_, record) => {
        const on = record.isStoneMarked === true;
        return (
          <Tooltip title={canToggleStoneMark ? (on ? 'Remove stone mark' : 'Mark stone') : 'Only admin/manager can change stone mark'}>
            <button
              type="button"
              className={`orders-stone-btn${on ? ' orders-stone-btn--on' : ''}`}
              aria-label={on ? 'Remove stone mark' : 'Mark stone'}
              aria-pressed={on}
              disabled={!canToggleStoneMark || stoneBusyId === record._id}
              onClick={(e) => handleToggleStoneMark(record, e)}
            >
              {on ? <DiamondFilled /> : <DiamondOutlined />}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 188,
      render: (val, record) => {
        const urls = Array.isArray(record.productImages) ? record.productImages : [];
        const thumb = typeof urls[0] === 'string' && urls[0].trim() ? urls[0].trim() : null;
        const go = (e) => {
          if (e.ctrlKey || e.metaKey) {
            window.open(`/orders/${record._id}`, '_blank');
          } else {
            navigate(`/orders/${record._id}`);
          }
        };
        return (
          <div className="orders-order-number-cell">
            {thumb ? (
              <button
                type="button"
                className="orders-order-thumb-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openProductImagePreview(urls);
                }}
                aria-label="View product photos"
              >
                <img src={thumb} alt="" className="orders-order-thumb" loading="lazy" />
              </button>
            ) : (
              <span className="orders-order-thumb orders-order-thumb--empty" aria-hidden />
            )}
            <span
              className="orders-cell-id orders-cell-id-link"
              role="button"
              tabIndex={0}
              onClick={go}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/orders/${record._id}`); } }}
            >
              {val || '—'}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (val) => <span className="orders-cell-customer">{val || '—'}</span>,
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 110,
      render: formatDate,
    },
    {
      title: 'Target date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      width: 110,
      render: formatDate,
    },
    {
      title: 'Source',
      dataIndex: 'sourceOfOrder',
      key: 'sourceOfOrder',
      width: 100,
      render: (s) => (
        <Tag className="orders-tag-source">{s || '—'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={STATUS_COLORS[s] || 'default'} className="orders-tag-status">
          {s === 'ready' ? 'Ready (send to QC)' : (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : 'Open')}
        </Tag>
      ),
    },
    {
      title: 'Order ready',
      dataIndex: 'orderReady',
      key: 'orderReady',
      width: 100,
      render: (s) => (
        <Tag
          color={
            s === 'Yes' ? 'green' : s === 'No' ? 'red' : 'default'
          }
          className="orders-tag-status"
        >
          {s || 'Incomplete'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  window.open(`/orders/${record._id}`, '_blank');
                } else {
                  navigate(`/orders/${record._id}`);
                }
              }}
            />
          </Tooltip>
          {canOrderEdit(user) && (
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    window.open(`/orders/${record._id}/edit`, '_blank');
                  } else {
                    navigate(`/orders/${record._id}/edit`);
                  }
                }}
              />
            </Tooltip>
          )}
          {isAdmin(user) && (
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                className="app-btn-delete"
                onClick={() => handleDeleteClick(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="orders-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="orders-header">
          <div className="orders-header-title-row">
            <Title level={3} style={{ margin: 0 }}>
              Orders
            </Title>
            <div className="orders-header-actions">
              <Button
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                className="orders-filter-btn"
                type="default"
              >
                Filter
              </Button>
              {openNewOrderModal && canCreateOrder && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openNewOrderModal}
                  className="orders-add-btn app-btn-primary"
                >
                  New order
                </Button>
              )}
            </div>
          </div>
          <Input
            placeholder="Search by customer or order #"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
            className="orders-search-input"
          />
        </div>

        <Card className="orders-card">
          <Table
            className="orders-table"
            columns={columns}
            dataSource={orders.map((row) => ({ ...row, key: row._id }))}
            rowClassName={(record) => {
              const excludedStatuses = ['return', 'dispatch', 'completed', 'cancelled'];
              const isPast = isTargetDatePast(record.targetDate);
              const isExcluded = excludedStatuses.includes(record.status);
              return isPast && !isExcluded ? 'orders-row-target-passed' : '';
            }}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} orders`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            responsive
            scroll={{ x: 900 }}
          />
        </Card>

        <Modal
          title="Filter orders"
          open={filterModalOpen}
          onCancel={() => setFilterModalOpen(false)}
          footer={[
            <Button key="clear" onClick={clearFilters} className="orders-filter-clear-btn">
              Clear
            </Button>,
            <Button key="cancel" onClick={() => setFilterModalOpen(false)} className="app-btn-cancel">
              Cancel
            </Button>,
            <Button key="apply" type="primary" onClick={applyFilters} className="app-btn-primary">
              Apply
            </Button>,
          ]}
          width={520}
          destroyOnClose
        >
          <div className="orders-filter-modal-content">
            <Form layout="vertical" className="orders-filter-form">
              <Form.Item label="Status">
                <Select
                  mode="multiple"
                  placeholder="All statuses"
                  allowClear
                  value={Array.isArray(modalFilters.status) ? modalFilters.status : modalFilters.status ? [modalFilters.status] : []}
                  onChange={(v) => setModalFilters((f) => ({ ...f, status: v?.length ? v : undefined }))}
                  options={STATUS_OPTIONS}
                  className="orders-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Order date">
                <Space.Compact style={{ width: '100%' }}>
                  <DatePicker
                    placeholder="Start"
                    value={modalFilters.orderDateStart ? dayjs(modalFilters.orderDateStart) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateStart: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                  <DatePicker
                    placeholder="End"
                    value={modalFilters.orderDateEnd ? dayjs(modalFilters.orderDateEnd) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateEnd: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                </Space.Compact>
              </Form.Item>
              <Form.Item label="Target date">
                <Space.Compact style={{ width: '100%' }}>
                  <DatePicker
                    placeholder="Start"
                    value={modalFilters.targetDateStart ? dayjs(modalFilters.targetDateStart) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, targetDateStart: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                  <DatePicker
                    placeholder="End"
                    value={modalFilters.targetDateEnd ? dayjs(modalFilters.targetDateEnd) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, targetDateEnd: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                </Space.Compact>
              </Form.Item>
              <Form.Item label="Source">
                <Select
                  placeholder="All sources"
                  allowClear
                  value={modalFilters.sourceOfOrder}
                  onChange={(v) => setModalFilters((f) => ({ ...f, sourceOfOrder: v }))}
                  options={SOURCE_OPTIONS}
                  className="orders-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Mode of parcel">
                <Input
                  placeholder="Mode of parcel"
                  value={modalFilters.modeOfParcel ?? ''}
                  onChange={(e) => setModalFilters((f) => ({ ...f, modeOfParcel: e.target.value || undefined }))}
                  allowClear
                />
              </Form.Item>
              <Form.Item label="Order ready">
                <Select
                  placeholder="All"
                  allowClear
                  value={modalFilters.orderReady}
                  onChange={(v) => setModalFilters((f) => ({ ...f, orderReady: v }))}
                  options={ORDER_READY_OPTIONS}
                  className="orders-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Custom order">
                <Checkbox
                  checked={modalFilters.isFavoriteOnly === true}
                  onChange={(e) =>
                    setModalFilters((f) => ({ ...f, isFavoriteOnly: e.target.checked ? true : undefined }))
                  }
                >
                  Show custom orders only
                </Checkbox>
              </Form.Item>
              <Form.Item label="Stone mark">
                <Checkbox
                  checked={modalFilters.isStoneMarkedOnly === true}
                  onChange={(e) =>
                    setModalFilters((f) => ({ ...f, isStoneMarkedOnly: e.target.checked ? true : undefined }))
                  }
                >
                  Show stone mark only
                </Checkbox>
              </Form.Item>
              <Form.Item label="Favourite">
                <Checkbox
                  checked={modalFilters.isImportantOnly === true}
                  onChange={(e) =>
                    setModalFilters((f) => ({ ...f, isImportantOnly: e.target.checked ? true : undefined }))
                  }
                >
                  Show favourites only
                </Checkbox>
              </Form.Item>
            </Form>
          </div>
        </Modal>

        <Modal
          open={orderImagePreview != null}
          onCancel={() => setOrderImagePreview(null)}
          footer={null}
          width="auto"
          centered
          destroyOnClose
          title={
            orderImagePreview && orderImagePreview.urls.length > 1
              ? `Photo ${orderImagePreview.index + 1} of ${orderImagePreview.urls.length}`
              : 'Product photo'
          }
          styles={{
            body: {
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            },
          }}
        >
          {orderImagePreview && orderImagePreview.urls.length > 0 && (
            <>
              {orderImagePreview.index > 0 && (
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={() =>
                    setOrderImagePreview((p) =>
                      p && p.index > 0 ? { ...p, index: p.index - 1 } : p
                    )
                  }
                />
              )}
              <img
                src={orderImagePreview.urls[orderImagePreview.index]}
                alt=""
                className="orders-list-preview-img"
              />
              {orderImagePreview.index < orderImagePreview.urls.length - 1 && (
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  onClick={() =>
                    setOrderImagePreview((p) =>
                      p && p.index < p.urls.length - 1 ? { ...p, index: p.index + 1 } : p
                    )
                  }
                />
              )}
            </>
          )}
        </Modal>

        <OrderDeleteModal
          open={orderToDelete}
          onCancel={() => setOrderToDelete(null)}
          onConfirm={handleDeleteConfirm}
          order={orderToDelete}
          loading={deleteLoading}
        />
      </Space>
    </div>
  );
}
